import get from 'lodash/get';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import API from './../api';
import {
    ONLINE_BOOKING_CLIENT_BY_META_RESERVATION_ID,
    ONLINE_BOOKING_CSS_VARIABLE_LIST_ENDPOINT,
    ONLINE_BOOKING_META_RESERVATION_BY_ID_ENDPOINT,
    ONLINE_BOOKING_META_RESERVATION_LIST_ENDPOINT,
    ONLINE_BOOKING_TECH_RESERVATION_BY_ID_ENDPOINT,
    ONLINE_BOOKING_META_RESERVATION_RENTAL_AGREEMENT_ENDPOINT,
    ONLINE_BOOKING_RENTAL_PROPERTY_BY_ID_ENDPOINT,
    ONLINE_BOOKING_SETTINGS_ENDPOINT,
    ONLINE_BOOKING_WEBSITE_BY_URL_ENDPOINT,
    ONLINE_BOOKING_COUNTRIES_ENDPOINT,
    ONLINE_BOOKING_STATES_ENDPOINT,
    ONLINE_BOOKING_META_RESERVATION_DISCOUNT_ENDPOINT,
    ONLINE_BOOKING_QUOTE_META_RESERVATION_BY_ID_ENDPOINT,
    ONLINE_BOOKING_META_RESERVATION_ONLINE_QUOTE_TEXT_ENDPOINT
} from './../endpoints';

export default {
    namespaced: true,

    state() {
        return {
            busy: false,
            client: null,
            cssVariables: null,
            reservation: null,
            rentalProperty: null,
            rentalAgreement: null,
            onlineQuoteText: '',
            rentalAgreementBusy: false,
            countries: [],
            states: [],
            settings: null,
            stripeClientSecret: null,
            website: null,
            isSignAgain: false,
        };
    },

    getters: {
        busy: state => state.busy,
        client: state => state.client,
        cssVariables: state => state.cssVariables,
        reservation: state => state.reservation,
        rentalProperty: state => state.rentalProperty,
        rentalAgreement: state => state.rentalAgreement,
        onlineQuoteText: state => state.onlineQuoteText,
        rentalAgreementBusy: state => state.rentalAgreementBusy,
        countries: state => state.countries,
        states: state => state.states,
        settings: state => state.settings,
        stripeClientSecret: state => state.stripeClientSecret,
        website: state => state.website,
        isSignAgain: state => state.isSignAgain,
    },

    mutations: {
        gotBusy(state, busy) {
            state.busy = busy;
        },
        gotClient(state, response) {
            state.client = response.data;
        },
        gotClientGuestCreditAmount(state, amount) {
            state.client = {
                ...state.client,
                guest_credit_amount: amount,
            };
        },
        gotStripeClientSecret(state, stripeClientSecret) {
            state.stripeClientSecret = stripeClientSecret;
        },
        gotClientIsSigned(state, isSigned) {
            state.client = {
                ...state.client,
                is_signed: isSigned,
            };
            if (isSigned) {
                state.isSignAgain = true;
            }
        },
        gotPablowData(state, pablowData) {
            state.reservation = {
                ...state.reservation,
                pablow_data: pablowData,
            };
        },
        gotPaymentMethodConfirmationStatus(state, paymentMethodConfirmationStatus) {
            state.reservation = {
                ...state.reservation,
                payment_method_confirmation_status: paymentMethodConfirmationStatus,
            };
        },
        gotCssVariables(state, response) {
            state.cssVariables = response.data;
        },
        gotReservation(state, response) {
            state.reservation = response.data;

            const stripeClientSecret = get(state.reservation, 'setup_intent.client_secret', null);
            if (!!stripeClientSecret) {
                state.stripeClientSecret = stripeClientSecret;
            }
        },
        gotRentalProperty(state, response) {
            state.rentalProperty = response.data;
        },
        gotRentalAgreement(state, response) {
            state.rentalAgreement = response.data.rental_agreement;
        },
        gotOnlineQuoteText(state, response) {
            state.onlineQuoteText = response.data.online_quote_text;
        },
        gotRentalAgreementBusy(state, busy) {
            state.rentalAgreementBusy = busy;
        },
        gotCountries(state, response) {
            state.countries = response.data.map(country => ({
                ...country,
                value: country.id,
                text: country.name,
            }));
        },
        gotStates(state, response) {
            state.states = response.data.map(state => ({
                ...state,
                value: state.id,
                text: state.name,
            }));
        },
        gotSettings(state, response) {
            state.settings = response.data;
        },
        gotWebsite(state, response) {
            state.website = response.data;
        },
        requireNewSignature(state) {
            state.client = {
                ...state.client,
                is_signed: false,
            };
        },
        syncClientWithReservation(state) {
            const client = { ...state.client };

            if (isNil(client.tech_reservations)) {
                client.tech_reservations = [];
            }

            client.tech_reservations = state.reservation.tech_reservations.reduce(
                (result, rtr) => {
                    const guests = rtr.property.is_children_rates
                        ? rtr.tech_reservation.guests +
                          rtr.tech_reservation.children
                        : rtr.tech_reservation.guests;

                    const ctr = client.tech_reservations.find(
                        ctr => ctr.id === rtr.tech_reservation.id
                    );
                    if (ctr) {
                        if (ctr.guest_names.length >= guests) {
                            return [
                                ...result,
                                {
                                    ...ctr,
                                    guest_names: ctr.guest_names.slice(
                                        0,
                                        guests
                                    ),
                                },
                            ];
                        }
                        return [
                            ...result,
                            {
                                ...ctr,
                                guest_names: ctr.guest_names.concat(
                                    range(
                                        0,
                                        guests - ctr.guest_names.length
                                    ).map(_ => ({
                                        name: '',
                                        age: null,
                                        email: '',
                                    }))
                                ),
                            },
                        ];
                    }

                    return [
                        ...result,
                        {
                            id: rtr.tech_reservation.id,
                            is_require_guest_age:
                                rtr.property.is_require_guest_age,
                            is_require_guest_email:
                                rtr.property.is_require_guest_email,
                            guest_names: range(0, guests).map(_ => ({
                                name: '',
                                age: null,
                                email: '',
                            })),
                        },
                    ];
                },
                []
            );

            state.client = client;
        },
        setClientIsAgreed(state, isAgreed) {
            state.client.is_agreed = isAgreed;
        },
        setInvoiceVariablesFormResults(state, results) {
            state.client = {
                ...state.client,
                invoice_variables_form_results: results
            }
        },
        setInvoiceVariablesFormErrors(state, errors) {
            state.client = {
                ...state.client,
                invoice_variables_form_errors: errors
            }
        },
    },

    actions: {
        async fetchCssVariables({ commit, state }, { preview = false }) {
            commit(
                'gotCssVariables',
                await API.get(
                    ONLINE_BOOKING_CSS_VARIABLE_LIST_ENDPOINT(
                        state.website.url
                    ) + `?preview=${preview}`
                )
            );
        },
        async createReservation(
            { commit, state, dispatch },
            {
                withRentalAgreement = true,
                withOnlineQuoteText = false,
                withClient = true,
                ...payload
            }
        ) {
            commit(
                'gotReservation',
                await API.post(
                    ONLINE_BOOKING_META_RESERVATION_LIST_ENDPOINT(
                        state.website.url
                    ),
                    payload
                )
            );
            if (withClient) {
                commit(
                    'gotClient',
                    await API.get(
                        ONLINE_BOOKING_CLIENT_BY_META_RESERVATION_ID(
                            state.website.url,
                            state.reservation.id
                        )
                    )
                );
            }
            commit('syncClientWithReservation');
            if (withRentalAgreement) dispatch('fetchRentalAgreement');
            if (withOnlineQuoteText) dispatch('fetchOnlineQuoteText');
        },
        async fetchReservation(
            { commit, state, dispatch },
            {
                id,
                withClient = false,
                withRentalAgreement = true,
                withOnlineQuoteText = false,
            }
        ) {
            commit(
                'gotReservation',
                await API.get(
                    ONLINE_BOOKING_META_RESERVATION_BY_ID_ENDPOINT(
                        state.website.url,
                        id
                    )
                )
            );
            if (withClient) {
                commit(
                    'gotClient',
                    await API.get(
                        ONLINE_BOOKING_CLIENT_BY_META_RESERVATION_ID(
                            state.website.url,
                            id
                        )
                    )
                );
                commit('syncClientWithReservation');
            }
            if (withRentalAgreement) dispatch('fetchRentalAgreement');
            if (withOnlineQuoteText) dispatch('fetchOnlineQuoteText');
        },
        async fetchQuoteReservation({ commit, state, dispatch }, { id, withClient = false  }) {
            commit(
                'gotReservation',
                await API.get(
                    ONLINE_BOOKING_QUOTE_META_RESERVATION_BY_ID_ENDPOINT(
                        state.website.url,
                        id
                    )
                )
            );
            if (withClient) {
                commit(
                    'gotClient',
                    await API.get(
                        ONLINE_BOOKING_CLIENT_BY_META_RESERVATION_ID(
                            state.website.url,
                            state.reservation.id,
                        )
                    )
                );
                commit('syncClientWithReservation');
                dispatch('fetchRentalAgreement');
            }
        },
        async fetchRentalProperty({ commit, state }, { url, id }) {
            commit(
                'gotRentalProperty',
                await API.get(
                    ONLINE_BOOKING_RENTAL_PROPERTY_BY_ID_ENDPOINT(
                        state.website.url,
                        id
                    )
                )
            );
        },
        async fetchRentalAgreement({ commit, state }) {
            commit('gotRentalAgreementBusy', true);
            try {
                commit(
                    'gotRentalAgreement',
                    await API.get(
                        ONLINE_BOOKING_META_RESERVATION_RENTAL_AGREEMENT_ENDPOINT(
                            state.website.url,
                            state.reservation.id
                        )
                    )
                );
            } finally {
                commit('requireNewSignature');
                commit('gotRentalAgreementBusy', false);
            }
        },
        async fetchOnlineQuoteText({ commit, state }) {
            commit(
                'gotOnlineQuoteText',
                await API.get(
                    ONLINE_BOOKING_META_RESERVATION_ONLINE_QUOTE_TEXT_ENDPOINT(
                        state.website.url,
                        state.reservation.id
                    )
                )
            );
        },
        async fetchCountries({ commit, state }) {
            commit(
                'gotCountries',
                await API.get(
                    ONLINE_BOOKING_COUNTRIES_ENDPOINT(state.website.url)
                )
            );
        },
        async fetchStates({ commit, state }, { countryId }) {
            commit(
                'gotStates',
                await API.get(
                    ONLINE_BOOKING_STATES_ENDPOINT(state.website.url, countryId)
                )
            );
        },
        async fetchSettings({ commit, state }) {
            commit(
                'gotSettings',
                await API.get(
                    ONLINE_BOOKING_SETTINGS_ENDPOINT(state.website.url)
                )
            );
        },
        async fetchWebsite({ commit }, { url, preview = false }) {
            commit(
                'gotWebsite',
                await API.get(
                    ONLINE_BOOKING_WEBSITE_BY_URL_ENDPOINT(url) +
                        `?preview=${preview}`
                )
            );
        },
        async deleteTechReservation({ commit, state, dispatch }, { id }) {
            commit(
                'gotReservation',
                await API.delete(
                    ONLINE_BOOKING_TECH_RESERVATION_BY_ID_ENDPOINT(
                        state.website.url,
                        id
                    )
                )
            );
            commit('syncClientWithReservation');
            dispatch('fetchRentalAgreement');
        },
        async updateTechReservation({ commit, state, dispatch }, techReservation) {
            commit(
                'gotReservation',
                await API.put(
                    ONLINE_BOOKING_TECH_RESERVATION_BY_ID_ENDPOINT(
                        state.website.url,
                        techReservation.tech_reservation.id
                    ),
                    techReservation
                )
            );
            commit('syncClientWithReservation');
            dispatch('fetchRentalAgreement');
        },
        async applyReservationDiscount({ commit, state, dispatch }, payload) {
            commit(
                'gotReservation',
                await API.post(
                    ONLINE_BOOKING_META_RESERVATION_DISCOUNT_ENDPOINT(
                        state.website.url,
                        state.reservation.id
                    ),
                    payload
                )
            );
            commit('syncClientWithReservation');
            dispatch('fetchRentalAgreement');
        },
    },
};
